import "core-js/modules/es.array.push.js";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.every.js";
import "core-js/modules/es.iterator.filter.js";
import "core-js/modules/es.iterator.find.js";
import "core-js/modules/es.iterator.map.js";
import api from '@/api';
import { ElMessage } from 'element-plus';
import DigitSymbolShape from './DigitSymbolShape.vue';
import MappingLegend from './MappingLegend.vue';
export default {
  name: 'DigitSymbolReactionSession',
  components: {
    DigitSymbolShape,
    MappingLegend
  },
  emits: ['finished'],
  props: {
    session: {
      type: Object,
      required: true
    },
    mode: {
      type: String,
      default: 'practice'
    }
  },
  data() {
    return {
      workingSession: {
        ...this.session
      },
      phase: 'intro',
      demoIndex: 0,
      demoLocked: false,
      demoSelected: '',
      demoFeedback: '',
      countdown: 3,
      currentIndex: 0,
      nextSeq: 1,
      acceptedThroughSeq: 0,
      pendingEvents: [],
      questionShownAtMs: 0,
      remainingMs: 300000,
      answerLocked: false,
      syncing: false,
      syncFailed: false,
      finishMessage: '请不要关闭页面',
      summary: {},
      errorMessage: '',
      timer: null,
      flushTimer: null,
      countdownTimer: null,
      visibilityChangeCount: 0,
      unsavedCount: 0
    };
  },
  computed: {
    currentQuestion() {
      return this.workingSession.questions?.[this.currentIndex];
    },
    progressPercent() {
      return Math.min(100, this.currentIndex * 100 / Math.max(1, this.workingSession.total || 300));
    },
    formattedRemaining() {
      const seconds = Math.max(0, Math.ceil(this.remainingMs / 1000));
      return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    },
    syncLabel() {
      if (this.syncFailed) return '网络不稳定，记录将在恢复后同步';
      if (this.syncing || this.pendingEvents.length) return '正在同步';
      return '记录已同步';
    },
    demoQuestions() {
      const baseMapping = this.workingSession.mapping || [];
      const shapes = baseMapping.map(item => item.shapeId);
      if (shapes.length !== 9) return [];
      return [1, 5, 9].map((digit, index) => {
        const mappingShift = index * 3;
        const mapping = baseMapping.map((item, digitIndex) => ({
          digit: item.digit,
          shapeId: shapes[(digitIndex + mappingShift) % shapes.length]
        }));
        const optionShift = index * 2 + 1;
        return {
          targetDigit: digit,
          mapping,
          optionShapeIds: shapes.slice(optionShift).concat(shapes.slice(0, optionShift))
        };
      });
    },
    demoQuestion() {
      return this.demoQuestions[this.demoIndex] || {
        targetDigit: 1,
        mapping: [],
        optionShapeIds: []
      };
    },
    storageKey() {
      return `digit-symbol-reaction:${this.workingSession.examId}`;
    }
  },
  mounted() {
    this.restoreQueue();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    if (this.session.state === 'TESTING') this.resumeActiveSession();
    if (this.session.state === 'END' || this.session.state === 'TIME_UP_END') {
      if (this.mode === 'mock') {
        this.phase = 'active';
        this.finish('RECOVER_FINISH');
      } else {
        this.showResult(this.session);
      }
    }
  },
  beforeUnmount() {
    this.clearTimers();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  methods: {
    startDemo() {
      this.phase = 'demo';
    },
    answerDemo(shapeId) {
      if (this.demoLocked) return;
      this.demoLocked = true;
      this.demoSelected = shapeId;
      const correctShape = this.demoQuestion.mapping?.find(item => item.digit === this.demoQuestion.targetDigit)?.shapeId;
      const correct = shapeId === correctShape;
      this.demoFeedback = correct ? '正确' : '再看一下对应关系';
      window.setTimeout(() => {
        if (this.demoIndex < 2) {
          this.demoIndex++;
          this.demoSelected = '';
          this.demoFeedback = '';
          this.demoLocked = false;
        } else {
          this.startCountdown();
        }
      }, correct ? 320 : 650);
    },
    demoOptionClass(shapeId) {
      if (!this.demoLocked || this.demoSelected !== shapeId) return '';
      const correctShape = this.demoQuestion.mapping?.find(item => item.digit === this.demoQuestion.targetDigit)?.shapeId;
      return shapeId === correctShape ? 'is-correct' : 'is-wrong';
    },
    startCountdown() {
      this.phase = 'countdown';
      this.countdown = 3;
      this.countdownTimer = window.setInterval(async () => {
        this.countdown--;
        if (this.countdown <= 0) {
          window.clearInterval(this.countdownTimer);
          await this.beginFormalTest();
        }
      }, 700);
    },
    async beginFormalTest() {
      try {
        const response = this.mode === 'mock' ? await api.beginMockReaction({
          sessionId: this.session.sessionId,
          unitOrder: this.session.unitOrder
        }) : await api.beginReaction(this.workingSession.examId);
        this.workingSession = {
          ...this.workingSession,
          ...response
        };
        this.currentIndex = response.current || 0;
        this.acceptedThroughSeq = response.acceptedThroughSeq || 0;
        this.nextSeq = Math.max(this.nextSeq, this.acceptedThroughSeq + 1);
        this.phase = 'active';
        this.questionShownAtMs = this.elapsedMs();
        this.startRuntimeTimers();
      } catch (error) {
        this.fail(error, '开始失败，请检查练习次数或网络后重试');
      }
    },
    async resumeActiveSession() {
      const needsQuestionRefresh = this.session.generatorVersion !== 2 || !this.session.questions?.every(question => Array.isArray(question.mapping));
      if (needsQuestionRefresh) {
        this.phase = 'finishing';
        this.finishMessage = '正在更新题目规则';
        try {
          const response = this.mode === 'mock' ? await api.beginMockReaction({
            sessionId: this.session.sessionId,
            unitOrder: this.session.unitOrder
          }) : await api.beginReaction(this.session.examId);
          this.workingSession = {
            ...this.workingSession,
            ...response
          };
        } catch (error) {
          this.fail(error, '题目规则已更新，请刷新页面后重试');
          return;
        }
      }
      this.phase = 'active';
      this.acceptedThroughSeq = this.workingSession.acceptedThroughSeq || 0;
      this.pendingEvents = this.pendingEvents.filter(event => event.seq > this.acceptedThroughSeq);
      const pendingNext = this.pendingEvents.length ? Math.max(...this.pendingEvents.map(event => event.questionIndex)) + 1 : 0;
      this.currentIndex = Math.max(this.workingSession.current || 0, pendingNext);
      this.nextSeq = Math.max(this.acceptedThroughSeq + 1, this.pendingEvents.length ? Math.max(...this.pendingEvents.map(event => event.seq)) + 1 : 1);
      this.questionShownAtMs = this.elapsedMs();
      this.startRuntimeTimers();
    },
    answer(selectedOptionIndex) {
      if (this.answerLocked || this.phase !== 'active' || !this.currentQuestion) return;
      this.answerLocked = true;
      const answeredAtMs = this.elapsedMs();
      const event = {
        seq: this.nextSeq++,
        questionIndex: this.currentIndex,
        selectedOptionIndex,
        questionShownAtMs: this.questionShownAtMs,
        answeredAtMs,
        reactionTimeMs: Math.max(1, Math.round(answeredAtMs - this.questionShownAtMs))
      };
      this.pendingEvents.push(event);
      this.persistQueue();
      this.currentIndex++;
      if (this.currentIndex >= (this.workingSession.total || 300)) {
        this.finish('ALL_ANSWERED');
        return;
      }
      this.$nextTick(() => {
        this.questionShownAtMs = this.elapsedMs();
        this.answerLocked = false;
      });
      if (this.pendingEvents.length >= 10) this.flush().catch(() => {});
    },
    async flush() {
      if (this.syncing || !this.pendingEvents.length || this.phase === 'result') return;
      this.syncing = true;
      const events = this.pendingEvents.slice(0, 30);
      const payload = this.batchPayload(events);
      try {
        const response = this.mode === 'mock' ? await api.submitMockReactionAnswers(payload) : await api.submitReactionAnswers(this.workingSession.examId, payload);
        this.acceptedThroughSeq = Math.max(this.acceptedThroughSeq, response.acceptedThroughSeq || 0);
        this.pendingEvents = this.pendingEvents.filter(event => event.seq > this.acceptedThroughSeq);
        this.syncFailed = false;
        this.persistQueue();
      } catch (error) {
        this.syncFailed = true;
        throw error;
      } finally {
        this.syncing = false;
      }
    },
    async drainQueue() {
      while (this.pendingEvents.length) await this.flush();
    },
    async finish(reason) {
      if (this.phase === 'finishing' || this.phase === 'result') return;
      this.phase = 'finishing';
      this.answerLocked = true;
      this.clearTimers();
      try {
        try {
          await this.drainQueue();
        } catch (error) {
          if (Date.now() <= (this.workingSession.endTimeStamp || 0) + 3500) throw error;
          this.unsavedCount = this.pendingEvents.length;
          this.pendingEvents = [];
          this.persistQueue();
        }
        const payload = {
          ...this.contextPayload(),
          lastSeq: this.nextSeq - 1,
          clientElapsedMs: Math.min(this.elapsedMs(), this.workingSession.durationMs || 300000),
          finishReason: reason
        };
        const response = this.mode === 'mock' ? await api.finishMockReaction(payload) : await api.finishReaction(this.workingSession.examId, payload);
        sessionStorage.removeItem(this.storageKey);
        if (this.mode === 'mock') {
          this.finishMessage = '本单元已保存';
          if (this.unsavedCount) ElMessage.warning(`有 ${this.unsavedCount} 题未能同步，已按未答处理`);
          this.$emit('finished', response);
        } else {
          this.showResult(response);
        }
      } catch (error) {
        this.finishMessage = '保存尚未完成，正在等待网络恢复';
        this.syncFailed = true;
        window.setTimeout(() => {
          if (this.phase === 'finishing') {
            this.phase = 'active';
            this.answerLocked = false;
            this.startRuntimeTimers();
          }
        }, 1500);
      }
    },
    showResult(response) {
      this.workingSession = {
        ...this.workingSession,
        ...response
      };
      this.summary = response.reactionSummary || {};
      this.phase = 'result';
      this.clearTimers();
    },
    startRuntimeTimers() {
      this.clearTimers();
      this.updateRemaining();
      this.timer = window.setInterval(this.updateRemaining, 200);
      this.flushTimer = window.setInterval(() => {
        this.flush().catch(() => {});
      }, 2000);
    },
    updateRemaining() {
      this.remainingMs = Math.max(0, (this.workingSession.endTimeStamp || 0) - Date.now());
      if (this.remainingMs <= 0 && this.phase === 'active') this.finish('TIME_UP');
    },
    elapsedMs() {
      return Math.max(0, Date.now() - (this.workingSession.startedAtTimestamp || this.workingSession.startTimeStamp || Date.now()));
    },
    batchPayload(events) {
      const first = events[0]?.seq || 0;
      const last = events[events.length - 1]?.seq || 0;
      return {
        ...this.contextPayload(),
        batchId: `${this.workingSession.examId}-${first}-${last}`,
        events,
        clientElapsedMs: this.elapsedMs(),
        visibilityChangeCount: this.visibilityChangeCount
      };
    },
    contextPayload() {
      return this.mode === 'mock' ? {
        sessionId: this.session.sessionId,
        unitOrder: this.session.unitOrder
      } : {};
    },
    persistQueue() {
      sessionStorage.setItem(this.storageKey, JSON.stringify({
        pendingEvents: this.pendingEvents,
        nextSeq: this.nextSeq,
        visibilityChangeCount: this.visibilityChangeCount
      }));
    },
    restoreQueue() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}');
        this.pendingEvents = Array.isArray(saved.pendingEvents) ? saved.pendingEvents : [];
        this.nextSeq = saved.nextSeq || 1;
        this.visibilityChangeCount = saved.visibilityChangeCount || 0;
      } catch (error) {
        sessionStorage.removeItem(this.storageKey);
      }
    },
    onVisibilityChange() {
      if (document.hidden && this.phase === 'active') {
        this.visibilityChangeCount++;
        this.persistQueue();
      }
    },
    onBeforeUnload() {
      if (!this.pendingEvents.length || this.mode === 'mock') return;
      const events = this.pendingEvents.slice(0, 10);
      const body = new Blob([JSON.stringify(this.batchPayload(events))], {
        type: 'application/json'
      });
      navigator.sendBeacon(`/user/exam/reaction/${this.workingSession.examId}/answers`, body);
    },
    clearTimers() {
      window.clearInterval(this.timer);
      window.clearInterval(this.flushTimer);
      window.clearInterval(this.countdownTimer);
      this.timer = null;
      this.flushTimer = null;
      this.countdownTimer = null;
    },
    review() {
      this.$router.push({
        name: 'ReviewAnswerView',
        params: {
          subject: 'DigitSymbolReaction',
          examId: this.workingSession.examId
        }
      });
    },
    retry() {
      this.errorMessage = '';
      this.phase = this.workingSession.state === 'TESTING' ? 'active' : 'intro';
      if (this.phase === 'active') this.startRuntimeTimers();
    },
    fail(error, fallback) {
      this.errorMessage = error?.message || fallback;
      this.phase = 'error';
      this.clearTimers();
    }
  }
};